import React, { useEffect, useState } from "react";
import styled from "styled-components";

interface MediaPreviewProps {
    url?: string;
}

const MediaPreview: React.FC<MediaPreviewProps> = ({ url }) => {
    const [mediaType, setMediaType] = useState<
        "audio" | "video" | "embed" | "unknown"
    >("unknown");

    const getYouTubeVideoId = (url: string) => {
        const match = url.match(
            /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
        );
        return match && match[7]?.length === 11 ? match[7] : null;
    };

    const getVimeoVideoId = (url: string) => {
        const match = url.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
        return match ? match[1] : null;
    };

    useEffect(() => {
        if (!url) {
            setMediaType("unknown");
            return;
        }

        if (url.includes("youtube.com") || url.includes("youtu.be")) {
            setMediaType("embed");
            return;
        }

        if (url.includes("vimeo.com")) {
            setMediaType("embed");
            return;
        }

        const audioExt = [".mp3", ".wav", ".ogg", ".m4a", ".aac"];
        const videoExt = [".mp4", ".webm", ".mov", ".avi", ".mkv", ".m3u8"];

        const ext = url.substring(url.lastIndexOf(".")).toLowerCase();

        if (audioExt.includes(ext)) setMediaType("audio");
        else if (videoExt.includes(ext)) setMediaType("video");
        else setMediaType("unknown");
    }, [url]);

    if (!url) {
        return <EmptyState>No media available</EmptyState>;
    }

    if (mediaType === "audio") {
        return (
            <AudioWrapper>
                <audio controls src={url} />
            </AudioWrapper>
        );
    }

    if (mediaType === "video") {
        return (
            <VideoWrapper>
                <video controls src={url} />
            </VideoWrapper>
        );
    }

    if (mediaType === "embed") {
        const yt = getYouTubeVideoId(url);
        const vimeo = getVimeoVideoId(url);

        if (yt) {
            return (
                <EmbedWrapper>
                    <iframe
                        src={`https://www.youtube.com/embed/${yt}`}
                        allowFullScreen
                    />
                </EmbedWrapper>
            );
        }

        if (vimeo) {
            return (
                <EmbedWrapper>
                    <iframe
                        src={`https://player.vimeo.com/video/${vimeo}`}
                        allowFullScreen
                    />
                </EmbedWrapper>
            );
        }
    }

    return (
        <Unsupported>
            Unsupported media link
        </Unsupported>
    );
};


const AudioWrapper = styled.div`
  width: 100%;
  margin-top: 8px;

  audio {
    width: 100%;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  margin-top: 8px;

  video {
    width: 100%;
    max-height: 400px;
    border-radius: 6px;
    background: #000;
  }
`;

const EmbedWrapper = styled.div`
  width: 100%;
  margin-top: 8px;

  iframe {
    width: 100%;
    height: 315px;
    border-radius: 6px;
    border: none;
    background: #000;
  }
`;

const Unsupported = styled.div`
  margin-top: 8px;
  padding: 10px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  color: #ad6800;
`;

const EmptyState = styled.div`
  font-size: 14px;
  color: #999;
`;


export default MediaPreview;
